import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import FileDownload from 'js-file-download';
import { store } from '../../redux/oldStore';
import { csvLine, splitCSV } from '../../utils/Tools';
import { ServerResponse } from '../../utils/Server';
import { ColumnData } from '../../utils/CommonTypes/CommonTypes';
import { UploadType } from '../Import/ParticipantsUpload';
import Modal from '../Modal/Modal';
import Icon, { IconType } from '../Icon/Icon';

const COLUMN_HEADERS = ['Entry ID', 'Participant First Name', 'Participant Last Name', 'Participant Email Address', 'Grade', 'Category Name', 'Entry Title', 'Team Project (true/false)', 'External Entry Id'];
const BASE_COLUMNS: ColumnData[] = COLUMN_HEADERS.map((h, i) => ({ index: i, id: `index:${i}`, value: h }));

interface IColumnMap {
  data: ColumnData;
  key: number;
}

interface ISchoolOption {
  id: number;
  name: string;
}

interface ILoadMyStudentsResult {
  allImported: boolean;
  failedToImport: string[];
  totalImported: number;
}

const toServerRow = (row: string[]): string[] => {
  const [entryId, firstName, lastName, email, grade, category, title, team, externalId] = row;
  return [entryId, firstName, lastName, email, grade, '', '', '', '', category, title, team, externalId ?? ''];
};

const RegisterStudents = () => {
  const [uploadType, setUploadType] = useState<UploadType>('comma');
  const [fileText, setFileText] = useState<string[][]>([]);
  const [parsingErrors, setParsingErrors] = useState<string[] | null>(null);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [sendSignInCredentials, setSendSignInCredentials] = useState(true);
  const [headersIncluded, setHeadersIncluded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [schools, setSchools] = useState<ISchoolOption[]>([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState<number>(-1);

  const [showMapColumns, setShowMapColumns] = useState<boolean>(false);
  const [columnMapping, setColumnMapping] = useState<Record<number, IColumnMap | undefined>>({});

  useEffect(() => {
    store.server
      .getApi<ServerResponse<{ schools: ISchoolOption[] }>>('../School/List')
      .then((res) => setSchools(res.Value.schools))
      .catch((err) => {
        console.log(err);
        toast.error(err);
      });
  }, []);

  const processTextFile = (text: string) => {
    const errors: string[] = [];
    const lines: string[][] = [];
    const formattedText = text.replaceAll('\r\n', '\n').replaceAll('\r', '\n');
    const rawLines = formattedText.split('\n');

    for (let i = 0; i < rawLines.length; i++) {
      const l = rawLines[i];
      if (l.length === 0) continue;

      const lineIndexes = csvLine(rawLines, i);
      let line = rawLines[lineIndexes[0]];
      for (let li = 1; li < lineIndexes.length; li++) {
        i = lineIndexes[li];
        line += '<br/>' + rawLines[lineIndexes[li]];
      }

      const lineValues = splitCSV(line, uploadType).map((v) => v.replace(/(\<br\/\>)/g, '\r\n'));

      if (lineValues.length === COLUMN_HEADERS.length || lineValues.length === COLUMN_HEADERS.length - 1) {
        lines.push(lineValues);
      } else {
        errors.push(`Line ${lineIndexes[0] + 1} has ${lineValues.length} columns, but should have ${COLUMN_HEADERS.length} (External Entry Id is optional)`);
        lines.push(lineValues);
      }
    }

    setFileText(lines);
    if (errors.length) {
      setParsingErrors(errors);
      setShowMapColumns(true);
    } else {
      setParsingErrors(null);
    }
  };

  const fileSelected = (e: any) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = (ev: any) => processTextFile(ev.target.result);
    reader.readAsText(file, 'utf-8-sig');
  };

  const processMappedFile = () => {
    const maps = Object.values(columnMapping).filter((m): m is IColumnMap => !!m);

    const indexList = BASE_COLUMNS.map((col) => {
      const map = maps.find((m) => m.data.id === col.id);
      return map?.key ?? -1;
    });

    const data = fileText.map((row) => indexList.map((i) => row[i] ?? ''));

    setFileText(data);
    setShowMapColumns(false);
    setParsingErrors(null);
  };

  const load = () => {
    const realText = headersIncluded ? fileText.slice(1) : fileText;
    store.server
      .postApi<ServerResponse<ILoadMyStudentsResult>>('../Import/LoadMyStudents', {
        ParticipantData: realText.map(toServerRow),
        SchoolId: selectedSchoolId,
        SendWelcomeEmail: sendWelcomeEmail,
        SendSignInCredentials: sendSignInCredentials,
      })
      .then((x) => {
        if (x.Success) {
          if (!x.Value.allImported) {
            toast.warning(
              <>
                We were not able to register everyone.
                <ul>
                  {x.Value.failedToImport.map((f, i) => (
                    <li key={`fti-${i}`}>{f}</li>
                  ))}
                </ul>
                Click to close this window
              </>,
              { autoClose: false },
            );
            setParsingErrors(x.Value.failedToImport);
          } else {
            toast.success('Your participants have been registered.');
          }
          setLoaded(true);
        } else {
          toast.error(x.Message, { autoClose: false });
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error(err);
      });
  };

  const downloadTemplateClick = () => {
    FileDownload(COLUMN_HEADERS.join(',') + '\r\n', 'Participant_upload_template.csv');
  };

  return (
    <div className="loading-container">
      <h1>Register Participants</h1>
      <div>
        Register all of your participants in one step using a CSV file. You can save Excel and Google Sheets files as CSV files.
        <ul>
          <li>
            <strong>Google Sheets</strong> Select File, then Download then as Comma-separated values (.csv)
          </li>
          <li>
            <strong>Excel</strong> Select File, Save as, then change type to CSV UTF-8 (*.csv)
          </li>
        </ul>
      </div>
      <div>
        <h3>Notes:</h3>
        <ul>
          <li>Each participant needs a unique First Name, Last Name, and Email combination</li>
          <li>If multiple participants are working on the same project, give them the same Entry ID and set Team Project to true</li>
          <li>Participants will be registered under your own teacher account, at the school you select below</li>
        </ul>
      </div>
      <div className="bumper-l">
        <label>School: </label>
        <select className="form-control" onChange={(e) => setSelectedSchoolId(+e.target.value)} value={selectedSchoolId}>
          <option value={-1}>Select a school...</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
      <div className="flex-between">
        <h4>File Format</h4>
        <button className="btn btn-secondary btn-xs" onClick={downloadTemplateClick}>
          Download Template
        </button>
      </div>

      {!loaded && (
        <div className="bumper-l">
          <div className="bumper-l">
            <label>Delimiter: </label>
            <select className="form-control" onChange={(e) => setUploadType(e.target.value as UploadType)} value={uploadType}>
              <option value={'comma'}>Comma</option>
              <option value={'tab'}>Tab</option>
            </select>
          </div>
          <div className="bumper-l">
            <label>Participant File:</label>
            <input type="file" accept=".csv" onChange={fileSelected} onClick={(evt: any) => (evt.target.value = null)} />
          </div>

          <div className="flex bumper-l">
            <label htmlFor="headers">Does the file include headers?</label>
            <input type="checkbox" name="headers" id="headers" className="form-input bumper-sides-out-l" onChange={(e) => setHeadersIncluded(e.target.checked)} defaultChecked={headersIncluded} />
          </div>

          {fileText && fileText.length > 0 && (
            <div className="csv-file-preview">
              <table>
                {!headersIncluded && (
                  <thead>
                    <tr>
                      {COLUMN_HEADERS.map((h, i) => (
                        <th key={i}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody>
                  {fileText.map((r, ri) => (
                    <tr key={'r' + ri}>
                      {r.map((v, vi) => {
                        if (headersIncluded && ri === 0) {
                          return <th key={ri + '-' + vi}>{v}</th>;
                        }
                        return <td key={ri + '-' + vi}>{v}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {parsingErrors && parsingErrors.length > 0 && (
            <table>
              <tbody>
                {parsingErrors.map((error, index) => (
                  <tr key={index}>
                    <td>{error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {fileText && fileText.length > 0 && (
            <div className="ready-to-load-area">
              <h4>If the participant file contents look good, press Register</h4>
              <div>
                Send welcome email: <input type="checkbox" defaultChecked={sendWelcomeEmail} onChange={(e) => setSendWelcomeEmail(e.target.checked)} />
                Send sign-in credentials: <input type="checkbox" defaultChecked={sendSignInCredentials} onChange={(e) => setSendSignInCredentials(e.target.checked)} />
              </div>
              {selectedSchoolId < 0 && <div className="text-warning">Select a school above before registering.</div>}
              <button type="button" className="btn btn-success" onClick={load} disabled={selectedSchoolId < 0}>
                <i className="fad fa-cloud-upload"></i> Register
              </button>
            </div>
          )}
        </div>
      )}

      {loaded && (
        <div>
          <h1>Participants registered!</h1>
          If you need to register more participants refresh your page.
        </div>
      )}

      {showMapColumns && (
        <Modal setModalOpen={setShowMapColumns} title="Map Columns to Load Data" size="l" noClose={true}>
          <div className="form-horizontal">
            <h4>
              <Icon type={IconType.mapO} />
              Map Columns to Data Points
            </h4>
            Map each column from your file to a data point.
            <div className="form-group">
              <div className="col-sm-offset-4 col-sm-8">
                <input type="checkbox" id="headers-2" className="" onChange={(e) => setHeadersIncluded(e.target.checked)} defaultChecked={headersIncluded} />
                <label htmlFor="headers-2" className="control-label">
                  First row are data headers
                </label>
              </div>
            </div>
            {fileText &&
              fileText.length > (headersIncluded ? 1 : 0) &&
              fileText[0].map((x, i) => {
                return (
                  <div className="form-group" key={i}>
                    <label className="col-sm-4 control-label" htmlFor={`map-column-${i}-svjj`}>
                      {headersIncluded ? (
                        <>
                          Column {i + 1}: <br />
                          {x}
                        </>
                      ) : (
                        <>
                          Column {i + 1}: <br />({x.substring(0, 25)})
                        </>
                      )}
                    </label>
                    <div className="col-sm-8">
                      <select
                        id={`map-column-${i}-svjj`}
                        className="form-control"
                        onChange={(x) => {
                          const mapping = columnMapping;
                          const index = +x.target.value;
                          if (index > -1) {
                            mapping[i] = { data: BASE_COLUMNS[index], key: i };
                          } else {
                            mapping[i] = undefined;
                          }
                          setColumnMapping({ ...mapping });
                        }}>
                        <option value="-1">Select data point...</option>
                        {BASE_COLUMNS.map((c, ci) => {
                          return (
                            <option key={`svjj-${i}-${ci}`} value={ci}>
                              {c.value}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                );
              })}
            <div className="form-group">
              <div className="col-sm-4"></div>
              <div className="col-sm-8">
                <button type={'button'} className="btn btn-secondary" onClick={processMappedFile}>
                  <Icon type={IconType.upload} /> Process File
                </button>
                <button
                  type={'button'}
                  className="btn btn-default"
                  onClick={() => {
                    setShowMapColumns(false);
                  }}>
                  <Icon type={IconType.close} /> Close
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default RegisterStudents;
